// Catalog.ID Client — the bitcash wallet-sync modes
// ============================================================================
// Per-mode dispatch for the three `registry-request` modes the BITCASH module
// sends (cid-encrypted-storage §7.3). `client.ts` routes here on
// `requesterId === 'bitcash'`.
//
// ── the boundary this file is ───────────────────────────────────────────────
// The member's wallet document lives in the vault's RESERVED `cid` namespace
// (§4.1), so the bitcash module cannot derive its key and cannot fetch it
// through the blob RPC — both gates refuse `cid` by name. §4.6 allows exactly
// one way across: a consent-gated release whose plaintext is wrapped to a key
// the requester minted for that one request. These three modes are that door,
// and the door is nailed to ONE requester: an envelope from anything but
// `bitcash` is refused `requester-not-permitted` before any gate runs, the way
// the CPR ceremony modes pin `cpr`.
//
// ── why release has a dialog and save does not (§7.3) ───────────────────────
// Release hands one machine spend capability for EVERY wallet the account
// holds, and it is asked BEFORE the unlock wait so a member who declines is
// never made to unlock for a refused act (the `export-mail-encrypt` ordering).
// Save brings ONE wallet the manager already holds INTO the member's list; the
// member sees it in Settings, and a dialog per imported wallet would train
// click-through on the surface where they most need to stay awake.
//
// ── gate stacks ─────────────────────────────────────────────────────────────
// release: required fields → wrap key decodes → digest recompute → 1600 bytes →
//          consent (or a standing grant) → wait-for-unlock → read the document →
//          wrap it → answer.
// save-prepare: mint, stash, answer. Nothing else: it publishes a public key.
// save:    required fields → stash lookup → requester match → digest recompute →
//          decapsulate → parse → validate the wallet → wait-for-unlock →
//          append with compare-and-set → answer with the slot.

import { sha512 } from '@noble/hashes/sha2.js';
import { canonicalize, type CanonicalValue } from '@catalog-canonical-json';
import * as aes from '../crypto/aes';
import {
    kemEncapsulate, kemDecapsulate,
    HKDF_INFO_BITCASH_WALLETS_RELEASE, HKDF_INFO_BITCASH_WALLETS_SAVE,
    HYBRID_PUBLIC_KEY_LEN,
} from '../crypto/kem';
import {
    BITCASH_WALLET_LABEL_MAX, type BitcashWalletSource,
} from '../crypto/bitcash-wallet-entry';
import {
    TYPE_CASH, extractType, toCompact, verifyWalletKey,
} from '../crypto/bitcash';
import type { NewWalletRecord } from '../crypto/bitcash-wallets-doc';
import { BlobQuotaError } from '../api/blobs';
import {
    prepareWalletSave, consumeWalletSavePrepare,
} from './bitcash-wallets-save-store';
import {
    BITCASH_WALLETS_RELEASE_POLICY_KEY, isBitcashWalletsMode,
    type BitcashWalletsDispatchDeps, type BitcashWalletsRequest, type BitcashWalletsResponse,
} from './bitcash-wallets-types';
import { ensureUnlocked } from './unlock-gate';

export { isBitcashWalletsMode };

/** The one module allowed to speak these modes (§7.3). */
export const BITCASH_WALLETS_REQUESTER = 'bitcash';

/** The label is display text from another process; cap it before rendering. */
const DEVICE_LABEL_MAX_CHARS = 64;

/** Where a saved wallet may say it came from. `registration` is refused: that
 *  source means "the entropy ceremony drew it", which only finalize can be. */
const SAVE_SOURCES: readonly BitcashWalletSource[] = ['import', 'settings'];

type ResponseBase = Pick<BitcashWalletsResponse, 'type' | 'to' | 'requestId'>;

function unavailable(base: ResponseBase, reason: string): BitcashWalletsResponse {
    return { ...base, status: 'unavailable', reason };
}

/**
 * Digest convention shared with the bitcash module and the relay's C# mirror
 * (`CrossModuleCanonical.ComputeWrapPubkeyDigest`): base64 SHA-512 over the RAW
 * bytes, never over their base64 text. The same recipe serves both committed
 * fields — the release's wrap key and the save's wrapped blob — because both
 * commit to "these exact bytes", and giving them separate recipes would buy
 * nothing but a second thing to get wrong.
 */
export function computeBitcashWalletsDigest(raw: Uint8Array): string {
    return aes.toBase64(sha512(raw));
}

/**
 * AAD for a release. Binds the wrap to the account it came from AND to the
 * GENERATION it carries: the document is mutable and one generation is
 * retained, so a manager that replayed an older release would otherwise be
 * unable to tell it from the current one.
 */
export function walletsReleaseAad(accountCid: string, generation: number): Uint8Array {
    return aes.encodeUtf8(`${accountCid}/bitcash-wallets-release/${generation}`);
}

/**
 * AAD for a save. Binds the wrapped wallet to the account and to the ONE
 * prepare leg whose ephemeral key opens it, so a blob minted against a
 * different prepare cannot be replayed into this one.
 */
export function walletsSaveAad(accountCid: string, prepareRequestId: string): Uint8Array {
    return aes.encodeUtf8(`${accountCid}/bitcash-wallets-save/${prepareRequestId}`);
}

export async function dispatchBitcashWalletsRequest(
    request: BitcashWalletsRequest,
    deps: BitcashWalletsDispatchDeps,
): Promise<BitcashWalletsResponse> {
    const base: ResponseBase = {
        type: 'registry-response',
        to: request.requesterId,
        requestId: request.requestId,
    };

    // Gate 1: the hub stamp. Defence in depth behind `client.ts`'s own check —
    // this dispatcher is reachable from a test and from any future caller, and
    // a family that releases spending keys must not depend on a caller's
    // diligence.
    if (!request._senderServiceId || request._senderServiceId !== request.requesterId) {
        return unavailable(base, 'sender-mismatch');
    }
    // Gate 2: the requester pin. §7.3 names one module; nothing else gets a
    // wallet key, whatever it registered as.
    if (request.requesterId !== BITCASH_WALLETS_REQUESTER) {
        return unavailable(base, 'requester-not-permitted');
    }
    // Gate 3: an identity to act for.
    const viewer = deps.getActiveCid();
    if (!viewer) return unavailable(base, 'no-active-identity');

    switch (request.mode) {
        case 'bitcash-wallets-release':
            return await handleRelease(request, base, viewer, deps);
        case 'bitcash-wallets-save-prepare':
            return handleSavePrepare(request, base, deps);
        case 'bitcash-wallets-save':
            return await handleSave(request, base, viewer, deps);
        default:
            return unavailable(base, 'unsupported-mode');
    }
}

// ── bitcash-wallets-release ─────────────────────────────────────────────────

async function handleRelease(
    request: BitcashWalletsRequest,
    base: ResponseBase,
    accountCid: string,
    deps: BitcashWalletsDispatchDeps,
): Promise<BitcashWalletsResponse> {
    // Gate 4.
    if (!request.wrapPubkey || !request.wrapPubkeyDigestBase64) {
        return unavailable(base, 'missing-required-field');
    }

    // Gate 5.
    let wrapPubkey: Uint8Array;
    try {
        wrapPubkey = aes.fromBase64(request.wrapPubkey);
    } catch {
        return unavailable(base, 'bad-wrap-key-base64');
    }

    // Gate 6: body↔canonical binding. Here the substituted body IS the attack —
    // release every wallet to a key the attacker holds.
    if (computeBitcashWalletsDigest(wrapPubkey) !== request.wrapPubkeyDigestBase64) {
        return unavailable(base, 'wrap-key-digest-mismatch');
    }

    // Gate 7.
    if (wrapPubkey.length !== HYBRID_PUBLIC_KEY_LEN) {
        return unavailable(base, 'wrap-key-not-hybrid');
    }

    // Gate 8: the universal unlock redirect (M2c, §10 item 5), and it runs
    // BEFORE the consent dialog rather than after it. A dialog rendered in a
    // tab the member is not looking at is invisible, and the focus-request is
    // the only thing that makes this tab visible — so when the bag is locked
    // the order has to be "raise the tab, wait for the unlock, then ask".
    // (When the bag is unlocked the gate is a no-op and the old order stands:
    // the sender's `requiresUserInteraction` already focused this tab.)
    // Bitcash's load-time release is exactly the case this ruling was written
    // for: it declares no interaction, and before M2c it died `cid-locked`.
    // Gate 8a: a dialog nobody can see is not consent. A standing grant for
    // this machine needs no dialog, so a quiet request (bitcash's load-time run
    // sends `requiresUserInteraction:false`) may ride on it. With NO grant the
    // dialog would open in a tab the hub was never asked to raise, and the
    // module would sit on it until its budget ran out — found live 2026-09-18,
    // when the member's own *Sync* then queued behind that run and never sent
    // the interactive request that would have focused this tab. Refused BEFORE
    // the unlock gate: raising the tab for a request refused on the next line
    // would be the yanked-between-tabs failure. The same rule
    // `bitcash-auth-token` applies; the module's explicit Sync sends `true`.
    const standing = deps.checkPolicy?.(
        accountCid, request.requesterId, BITCASH_WALLETS_RELEASE_POLICY_KEY) ?? 'none';
    if (standing === 'none' && request.requiresUserInteraction !== true) {
        return unavailable(base, 'consent-required');
    }

    if (!await ensureUnlocked(request, deps)) return unavailable(base, 'cid-locked');

    // Gate 9: consent, before the document is touched. A standing grant for
    // this machine short-circuits it (see the modal's header for why this mode
    // may be remembered when the mail export may not).
    if (standing === 'none') {
        if (!deps.confirmWalletsRelease) {
            // Wired in by `key-grant/client.ts`. A build that cannot ask must
            // not hand out spending capability.
            return unavailable(base, 'consent-ui-unavailable');
        }
        const deviceLabel = typeof request.deviceLabel === 'string' && request.deviceLabel.length > 0
            ? request.deviceLabel.slice(0, DEVICE_LABEL_MAX_CHARS)
            : undefined;
        const decision = await deps.confirmWalletsRelease({
            owningCid: accountCid,
            requesterId: request.requesterId,
            deviceLabel,
            wrapKeyFingerprint: toHexLower(sha512(wrapPubkey)),
        });
        if (decision === 'decline') {
            return { ...base, status: 'denied', reason: 'bitcash-wallets-release-declined' };
        }
        const entry = {
            requesterId: request.requesterId,
            keyId: BITCASH_WALLETS_RELEASE_POLICY_KEY,
            purpose: 'bitcash-wallets-release',
        };
        if (decision === 'always') deps.grantAlways?.(accountCid, entry);
        else if (decision === 'session') deps.grantSession?.(accountCid, entry);
    }

    // Gate 10: the document.
    let held: { doc: unknown; generation: number } | null;
    try {
        held = await deps.loadWallets();
    } catch (err) {
        return unavailable(base, blobFailureReason(err));
    }
    // REVOKED wallets ride along on purpose — the manager must know to retire
    // them, which it cannot learn from an absence. What is refused is an
    // account with no document, or one holding nothing at all.
    const document = held?.doc as { wallets?: unknown[] } | undefined;
    if (!held || !Array.isArray(document?.wallets) || document.wallets.length === 0) {
        return unavailable(base, 'no-wallets');
    }

    // Gate 11: wrap. The plaintext is the RFC 8785 canonical JSON of the whole
    // document — byte-identical to what the blob store holds, so the module and
    // the vault agree on what "the document" is without a second serializer.
    const plaintext = canonicalize(held.doc as CanonicalValue);
    const wrapped = await kemEncapsulate(
        wrapPubkey, plaintext,
        HKDF_INFO_BITCASH_WALLETS_RELEASE, walletsReleaseAad(accountCid, held.generation));

    return {
        ...base,
        status: 'approved',
        generation: held.generation,
        wrappedDocument: aes.toBase64(wrapped),
    };
}

// ── bitcash-wallets-save-prepare ────────────────────────────────────────────

function handleSavePrepare(
    request: BitcashWalletsRequest,
    base: ResponseBase,
    deps: BitcashWalletsDispatchDeps,
): BitcashWalletsResponse {
    // No fields, no consent, no keybag: the answer is a PUBLIC key, and minting
    // one costs nothing an attacker could not do for itself. The decision this
    // leg makes is only "remember the secret under this requestId, for this
    // requester, for five minutes".
    const publicKey = deps.prepareSave
        ? deps.prepareSave(request.requestId, request.requesterId)
        : prepareWalletSave(request.requestId, request.requesterId, deps.keygen);
    return { ...base, status: 'approved', cidWrapPubkey: aes.toBase64(publicKey) };
}

// ── bitcash-wallets-save ────────────────────────────────────────────────────

async function handleSave(
    request: BitcashWalletsRequest,
    base: ResponseBase,
    accountCid: string,
    deps: BitcashWalletsDispatchDeps,
): Promise<BitcashWalletsResponse> {
    // Gate 4.
    if (!request.prepareRequestId || !request.wrappedWallet || !request.wrappedWalletDigestBase64) {
        return unavailable(base, 'missing-required-field');
    }

    // Gate 5: the stash. One prepare leg opens exactly one save, so a replay
    // finds nothing — `consume` removes the entry whether or not it is spent.
    const consume = deps.consumeSave ?? ((id) => consumeWalletSavePrepare(id));
    const stash = consume(request.prepareRequestId);
    if (!stash) return unavailable(base, 'prepare-expired');

    // Gate 6: the prepare leg belonged to THIS requester. The requestId is 128
    // bits the module chose, so this is belt and braces on the hub stamp — but
    // a prepare is answered to any registered module that asks.
    if (stash.requesterId !== request.requesterId) {
        return unavailable(base, 'requester-mismatch');
    }

    // Gate 7: the wrapped blob decodes, and matches what the signature covered.
    // Here the substituted body IS the attack: a wallet the member never had,
    // filed under their account and offered for spending in Settings.
    let wrapped: Uint8Array;
    try {
        wrapped = aes.fromBase64(request.wrappedWallet);
    } catch {
        // Nothing to unwrap. Reported as the unwrap failure it is rather than
        // as a separate reason the module would have to learn.
        return unavailable(base, 'unwrap-failed');
    }
    if (computeBitcashWalletsDigest(wrapped) !== request.wrappedWalletDigestBase64) {
        return unavailable(base, 'wrapped-wallet-digest-mismatch');
    }

    // Gate 8: decapsulate under this account and this prepare leg.
    let plaintext: Uint8Array;
    try {
        plaintext = await kemDecapsulate(
            stash.secretKey, wrapped,
            HKDF_INFO_BITCASH_WALLETS_SAVE, walletsSaveAad(accountCid, request.prepareRequestId));
    } catch {
        return unavailable(base, 'unwrap-failed');
    } finally {
        stash.secretKey.fill(0);
    }

    // Gates 9-10: parse and validate. Everything the document codec would
    // check, checked here first, so a refusal names the wire field rather than
    // arriving as a decode error three layers down.
    let parsed: unknown;
    try {
        parsed = JSON.parse(aes.decodeUtf8(plaintext));
    } catch {
        return unavailable(base, 'bad-wallet');
    } finally {
        plaintext.fill(0);
    }
    const wallet = parsed as Record<string, unknown> | null;
    if (typeof wallet !== 'object' || wallet === null || Array.isArray(wallet)) {
        return unavailable(base, 'bad-wallet');
    }
    if (typeof wallet.key !== 'string' || !verifyWalletKey(wallet.key)) {
        return unavailable(base, 'bad-wallet');
    }
    const key = toCompact(wallet.key);
    if (extractType(key) !== TYPE_CASH) return unavailable(base, 'bad-wallet');
    if (typeof wallet.label !== 'string' || wallet.label.length > BITCASH_WALLET_LABEL_MAX) {
        return unavailable(base, 'bad-wallet');
    }
    if (!SAVE_SOURCES.includes(wallet.source as BitcashWalletSource)) {
        return unavailable(base, 'bad-wallet');
    }
    // No operator check: a code carries no operator (docs/cid-governance.md M13;
    // Bitcash Ledger Specification v2 §2.4), so any valid CASH code is filed,
    // whatever its second group, and nothing is stored beside it.

    // Gate 11: the unlock redirect. After the validation, so a malformed
    // wallet is refused without raising the member's tab for nothing. There is
    // no dialog on this leg, so nothing needs to follow the unlock.
    if (!await ensureUnlocked(request, deps)) return unavailable(base, 'cid-locked');

    // Gate 12: append. The store lands the compare-and-set retry, so a rival
    // device's wallet survives and the slot is recomputed against whatever won.
    const record: NewWalletRecord = {
        key,
        label: wallet.label,
        createdAt: Math.floor((deps.now?.() ?? Date.now()) / 1000),
        source: wallet.source as BitcashWalletSource,
        status: 'active',
    };
    try {
        const { slot, generation } = await deps.addWallet(record);
        return { ...base, status: 'approved', slot, generation };
    } catch (err) {
        return unavailable(base, blobFailureReason(err));
    }
}

// ── shared ──────────────────────────────────────────────────────────────────

/**
 * Map a blob-layer failure onto this family's vocabulary. The three the module
 * can act on are named; anything else is `save-failed`, which is the honest
 * answer for a network error or a node that refused for a reason this build
 * does not know about.
 */
function blobFailureReason(err: unknown): string {
    if (err instanceof BlobQuotaError) return 'quota-exceeded';
    const message = err instanceof Error ? err.message : String(err);
    if (message === 'cid-locked') return 'cid-locked';
    if (message === 'derive-root-missing' || message === 'derive-root-malformed') {
        return 'derive-root-missing';
    }
    return 'save-failed';
}

function toHexLower(bytes: Uint8Array): string {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
        const b = bytes[i] as number;
        out += (b < 16 ? '0' : '') + b.toString(16);
    }
    return out;
}
